
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Dashboard monitoring',
  layout: 'guide',
  description: 'Track integration health, metrics, and issues in the Data Partner Dashboard',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Dashboard monitoring' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "integration-metrics-and-health"
      }}>{`Integration metrics and health`}</h2>
      <p>{`Once your integration is live, the Overview page in the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/dashboard-overview"
        }}>{`Data Partner Dashboard`}</a>{` provides real-time health metrics. These metrics are not visible until production traffic is enabled.`}</p>
      <Image src='/assets/img/core-exchange/overview.png' alt='Dashboard overview page showing integration health metrics' caption="Monitor your integration's performance after going live" expandable={true} mdxType="Image" />
      <h3 {...{
        "id": "conversion-rate"
      }}>{`Conversion rate`}</h3>
      <ul>
        <li parentName="ul"><strong parentName="li">{`What it measures:`}</strong>{` How often users who select your institution successfully complete account linking. This metric covers activity between institution selection in Plaid Link and handoff back to Plaid.`}</li>
        <li parentName="ul"><strong parentName="li">{`Formula:`}</strong>{` (Number of Link sessions that reach handoff) / (Number of institution selects in Link)`}</li>
      </ul>
      <Image src='/assets/img/core-exchange/cx-dashboard-overview-flow.png' alt='Data flow from Link open through search, institution selection, handoff, token exchange, and data pulls' caption='Conversion is evaluated between Select institution and Handoff' expandable={true} mdxType="Image" />
      <h3 {...{
        "id": "connection-health"
      }}>{`Connection health`}</h3>
      <ul>
        <li parentName="ul"><strong parentName="li">{`What it measures:`}</strong>{` The overall success rate of Plaid's data extractions from your API after an account has been linked. Unlike conversion rate, connection health focuses on post-Link activity and is evaluated at token exchange and data pulls.`}</li>
      </ul>
      <h4 {...{
        "id": "traffic-types"
      }}>{`Traffic types`}</h4>
      <p>{`Plaid sends two kinds of traffic to your API:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`User-initiated traffic:`}</strong>{` Triggered when a user is actively going through a login or update flow. Account and transaction data are pulled in real time and made available to apps.`}</li>
        <li parentName="ul"><strong parentName="li">{`Batch traffic:`}</strong>{` Scheduled, automated requests that keep financial data (for example, transactions and balances) up to date even when the user is not active.`}</li>
      </ul>
      <h5 {...{
        "id": "what-plaid-calls-and-when"
      }}>{`What Plaid calls, and when`}</h5>
      <p>{`When a user first links an account, Plaid calls `}<inlineCode parentName="p">{`GET /accounts`}</inlineCode>{` to establish the linked accounts. Plaid also needs to identify the customer, and `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-server-setup#how-to-provide-it"
        }}>{`uses the first method you make available`}</a>{`: the `}<inlineCode parentName="p">{`sub`}</inlineCode>{` claim in the ID token, then your `}<inlineCode parentName="p">{`userinfo_endpoint`}</inlineCode>{`, then `}<inlineCode parentName="p">{`GET /customers/current`}</inlineCode>{`. For example, an OIDC provider that omits `}<inlineCode parentName="p">{`sub`}</inlineCode>{` will see a `}<inlineCode parentName="p">{`userinfo`}</inlineCode>{` call, and plain OAuth 2.0 providers will see `}<inlineCode parentName="p">{`GET /customers/current`}</inlineCode>{`. The products enabled for that user determine which additional FDX resource endpoints Plaid calls, e.g. `}<inlineCode parentName="p">{`GET /accounts/{accountId}/transactions`}</inlineCode>{` for Transactions.`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`User-initiated traffic`}</strong>{` pulls the FDX endpoints for a user's enabled products in real time, during an active login or update flow.`}</li>
        <li parentName="ul"><strong parentName="li">{`Batch traffic`}</strong>{` re-pulls those same endpoints automatically after linking, at a cadence that depends on the product and can vary by institution. By default, Plaid extracts `}<a parentName="li" {...{
            "href": "/docs/transactions#transactions-updates"
          }}>{`Transactions`}</a>{` and `}<a parentName="li" {...{
            "href": "/docs/investments"
          }}>{`Investments`}</a>{` data every 6 hours (up to four times a day), and `}<a parentName="li" {...{
            "href": "/docs/liabilities"
          }}>{`Liabilities`}</a>{` data once a day; if you require a different cadence, contact Plaid.`}</li>
      </ul>
      <h5 {...{
        "id": "scope"
      }}>{`Scope`}</h5>
      <p>{`"Scope" in Integration Health and the traffic metrics above refers to the FDX resource endpoints Plaid calls for a connection (Accounts, Transactions, etc.), not the OAuth `}<inlineCode parentName="p">{`scope`}</inlineCode>{` parameter your authorization server evaluates during the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication/oauth-flow"
        }}>{`OAuth flow`}</a>{`. A connection's scope reflects the products enabled for that user, and expands or narrows as those products change.`}</p>
      <h4 {...{
        "id": "result-classification"
      }}>{`Result classification`}</h4>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Success:`}</strong>{` Plaid did not encounter any errors during data extraction while interacting with your API.`}</li>
        <li parentName="ul"><strong parentName="li">{`Error:`}</strong>{` Plaid encountered an error at any point during data extraction. While many errors come from non-200 responses from your API, certain 200 responses can still be classified as errors if the extraction fails.`}</li>
      </ul>
      <h4 {...{
        "id": "health-score"
      }}>{`Health score`}</h4>
      <p>{`The proportion of successful data extractions across your API's data resource endpoints:`}</p>
      <p><strong parentName="p">{`(Number of successful extractions) / (Total extractions)`}</strong></p>
      <h3 {...{
        "id": "performance"
      }}>{`Performance`}</h3>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Uptime:`}</strong>{` Calculated as the percentage of successful and client-error responses (HTTP 2xx, 3xx, and 4xx) out of all received responses, excluding scheduled downtime (as indicated by FDX 503 errors).`}</li>
        <li parentName="ul"><strong parentName="li">{`Latency:`}</strong>{` The round-trip response time when Plaid calls your endpoints from the `}<inlineCode parentName="li">{`us-east-1`}</inlineCode>{` (Virginia) region.`}</li>
      </ul>
      <p>{`For target metric values, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/post-launch#target-metrics"
        }}>{`Post-launch operations`}</a>{`.`}</p>
      <h3 {...{
        "id": "detailed-logs"
      }}>{`Detailed logs`}</h3>
      <p>{`Click `}<strong parentName="p">{`View all`}</strong>{` to see detailed logs of all errors encountered during data extraction. Logs appear near-real-time — typically within seconds of the request completing — and are retained for 6 weeks before rolling up into the aggregate uptime and latency metrics above, so use them to investigate recent activity rather than as a long-term audit trail.`}</p>
      <Image src='/assets/img/core-exchange/detailed-logs.png' alt='Detailed error logs showing error types, timestamps, and request details' caption='View detailed logs of all errors encountered during data extraction' expandable={true} mdxType="Image" />
      <h2 {...{
        "id": "issue-center"
      }}>{`Issue Center`}</h2>
      <p>{`The Issue Center allows you to monitor and resolve integration issues that Plaid detects across your Core Exchange connections. When a new issue is detected, Plaid sends an email alert with a link to the issue in your dashboard. To access the Issue Center, navigate to `}<strong parentName="p">{`Open Finance → Issue Center`}</strong>{` in the sidebar.`}</p>
      <h3 {...{
        "id": "issue-center-page"
      }}>{`Issue Center page`}</h3>
      <p>{`The Issue Center page provides an overview of all active integration issues. Summary cards at the top display the total number of active issues and total errors. A bar chart shows error volume over time, with filters for error type, status, time range, and institution (platforms only).`}</p>
      <p>{`The main table lists each issue along with affected institutions, error count, detection date, and current status. Click any row to open the detail page for that issue.`}</p>
      <Image src='/assets/img/core-exchange/dashboard-issue-center.png' alt='Issue Center page showing summary cards, error volume chart, and issues table' caption='The Issue Center provides an overview of all active integration issues' expandable={true} mdxType="Image" />
      <h3 {...{
        "id": "issue-detail-page"
      }}>{`Issue detail page`}</h3>
      <p>{`The issue detail page shows the full context for a specific issue, including:`}</p>
      <ul>
        <li parentName="ul">{`Detection timestamp`}</li>
        <li parentName="ul">{`Number of impacted users and endpoints`}</li>
        <li parentName="ul">{`24-hour error count`}</li>
        <li parentName="ul">{`Current status`}</li>
        <li parentName="ul">{`Error trend over the last 24 hours or 7 days`}</li>
      </ul>
      <p>{`The `}<strong parentName="p">{`Sample errors`}</strong>{` section lists individual occurrences with the timestamp, institution, request ID, user ID, and endpoint. Use these details to cross-reference with your server logs. Use the `}<strong parentName="p">{`Assign status`}</strong>{` button to update the issue as your team triages and resolves it.`}</p>
      <Image src='/assets/img/core-exchange/dashboard-issue-center-detail.png' alt='Issue detail page showing error trend chart, impacted users, and sample errors table' caption='Drill into an issue to see error trends and sample errors' expandable={true} mdxType="Image" />
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "integration-metrics-and-health",
        level: 2,
        title: "Integration metrics and health",
      },
{
        id: "conversion-rate",
        level: 3,
        title: "Conversion rate",
      },
{
        id: "connection-health",
        level: 3,
        title: "Connection health",
      },
{
        id: "traffic-types",
        level: 4,
        title: "Traffic types",
      },
{
        id: "result-classification",
        level: 4,
        title: "Result classification",
      },
{
        id: "health-score",
        level: 4,
        title: "Health score",
      },
{
        id: "performance",
        level: 3,
        title: "Performance",
      },
{
        id: "detailed-logs",
        level: 3,
        title: "Detailed logs",
      },
{
        id: "issue-center",
        level: 2,
        title: "Issue Center",
      },
{
        id: "issue-center-page",
        level: 3,
        title: "Issue Center page",
      },
{
        id: "issue-detail-page",
        level: 3,
        title: "Issue detail page",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
